// src/app/api/eventApi.ts
//
// Registration-only API slice.
//
// Event metadata (name, date, speakers, agenda, colours etc.) is NOT
// fetched from the backend — it lives in src/data/eventData.json and
// is imported statically by EventLandingPage and EventRegistration.
//
// The types for event content (EventData, Speaker, AgendaItem) are
// defined in src/types/eventTypes.ts so they can be shared between
// the frontend components and this API slice without coupling the
// API slice to the backend's event schema.

import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   REGISTRATION TYPES
───────────────────────────────────────────────────────────────── */

export interface EventRegistration {
  id: string;
  eventSlug: string;
  eventName?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNo?: string | null;
  regionId?: string | null;
  regionName?: string | null;
  /** Alias of regionName — kept for backwards compatibility */
  region: string;
  provinceId?: string | null;
  provinceName?: string | null;
  /** Alias of provinceName */
  province?: string | null;
  extraFields?: Record<string, string>;
  privacyConsent: boolean;
  consentTimestamp: string;
  status: "registered" | "waitlisted" | "cancelled" | "attended";
  confirmationCode: string;
  hasSentCertificate: boolean;
  certificateSentAt?: string | null;
  createdAt: string;
}

export interface RegionDistribution {
  region: string;
  count: number;
}

export interface ProvinceDistribution {
  province: string;
  region: string;
  count: number;
}

export interface StatusDistribution {
  status: string;
  count: number;
}

export interface CertificateDistribution {
  status: "sent" | "pending";
  count: number;
}

/** Per-province entry in the byProvince attendance chart.
 *  province is null when the registrant had no province set. */
export interface ProvinceAttendance {
  province: string | null;
  provinceLabel: string;
  region: string;
  count: number;
  hasProvince: boolean;
}

/** Per-province comparative row: registered vs attended vs certificates */
export interface ComparisonRow {
  province: string | null;
  provinceLabel: string;
  region: string;
  registered: number;
  attended: number;
  attendanceRate: number; // percentage, 0-100, 1 decimal place
  certSent: number;
  certPending: number;
}

/** A single region returned by GET /api/events/regions */
export interface EventRegion {
  id: string;
  name: string;
}

/** A single province returned by GET /api/events/regions/:regionId/provinces */
export interface EventProvince {
  id: string;
  name: string;
}

/* ── Request shapes ── */

export interface RegisterForEventRequest {
  /** Must match the `slug` field in eventData.json */
  slug: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNo?: string;
  /** ObjectId string of the Region document */
  regionId: string;
  /** ObjectId string of the Province document (optional) */
  provinceId?: string;
  /** Event name from eventData.json — denormalised on the backend */
  eventName: string;
  privacyConsent: true;
  extraFields?: Record<string, string>;
}

export type SortBy = "province" | "region" | "createdAt";

export interface RegistrationFilters {
  eventName?: string;
  status?: string;
  regionName?: string;
  provinceName?: string;
  search?: string;
  sortBy?: SortBy;
  /** When false, only returns registrants who have NOT yet received a certificate */
  hasSentCertificate?: boolean;
  page?: number;
  limit?: number;
}

export interface AttendanceFilters {
  regionName?: string;
  provinceName?: string;
  search?: string;
  sortBy?: SortBy;
  hasSentCertificate?: boolean;
  page?: number;
  limit?: number;
}

/* ── Response shapes ── */

interface RegisterResponse {
  success: boolean;
  message: string;
  data: {
    confirmationCode: string;
    status: string;
    eventName: string;
    eventSlug: string;
  };
}

interface RegistrationsResponse {
  success: boolean;
  total: number;
  page: number;
  pages: number;
  count: number;
  data: EventRegistration[];
  charts: {
    byRegion: RegionDistribution[];
    byProvince: ProvinceDistribution[];
    byStatus: StatusDistribution[];
  };
}

export interface AttendanceResponse {
  success: boolean;
  total: number;
  page: number;
  pages: number;
  count: number;
  /** Unfiltered total attended — always the full event count */
  totalAttended: number;
  /** How many attended have hasSentCertificate: true */
  certificateSentCount: number;
  data: EventRegistration[];
  charts: {
    byRegion: RegionDistribution[];
    /** Real provinces only — null province bucket labelled "No province recorded" */
    byProvince: ProvinceAttendance[];
    byCertificate: CertificateDistribution[];
    /** Comparative analysis: registered vs attended per province */
    byComparison: ComparisonRow[];
  };
}

interface RegistrationResponse {
  success: boolean;
  data: EventRegistration;
}

interface MessageResponse {
  success: boolean;
  message: string;
}

interface EventRegionsResponse {
  success: boolean;
  count: number;
  data: EventRegion[];
}

interface EventProvincesResponse {
  success: boolean;
  count: number;
  data: EventProvince[];
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const eventApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/events/regions  — public: all non-archived regions for the form dropdown
    getEventRegions: builder.query<EventRegionsResponse, void>({
      query: () => "/events/regions",
      providesTags: [{ type: "Region" as const, id: "EVENT_FORM_LIST" }],
    }),

    // GET /api/events/regions/:regionId/provinces  — public: provinces for a region
    getEventProvinces: builder.query<EventProvincesResponse, string>({
      query: (regionId) => `/events/regions/${regionId}/provinces`,
      providesTags: (_r, _e, regionId) => [
        { type: "Province" as const, id: `EVENT_${regionId}` },
      ],
    }),

    // POST /api/events/:slug/register  — public registration
    // The `slug` in the URL comes from RegisterForEventRequest.slug.
    registerForEvent: builder.mutation<
      RegisterResponse,
      RegisterForEventRequest
    >({
      query: ({ slug, ...body }) => ({
        url: `/events/${slug}/register`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { slug }) => [
        { type: "EventRegs" as const, id: slug },
      ],
    }),

    // GET /api/events/:slug/registrations  — admin: list with charts
    getEventRegistrations: builder.query<
      RegistrationsResponse,
      { eventSlug: string } & RegistrationFilters
    >({
      query: ({ eventSlug, ...params }) => ({
        url: `/events/${eventSlug}/registrations`,
        params,
      }),
      providesTags: (_r, _e, { eventSlug }) => [
        { type: "EventRegs" as const, id: eventSlug },
      ],
    }),

    // PATCH /api/events/:slug/registrations/:regId  — admin: status update
    updateRegistrationStatus: builder.mutation<
      RegistrationResponse,
      { eventSlug: string; regId: string; status: string }
    >({
      query: ({ eventSlug, regId, status }) => ({
        url: `/events/${eventSlug}/registrations/${regId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { eventSlug }) => [
        { type: "EventRegs" as const, id: eventSlug },
      ],
    }),

    // DELETE /api/events/:slug/registrations/:regId  — admin: hard delete
    deleteRegistration: builder.mutation<
      MessageResponse,
      { eventSlug: string; regId: string }
    >({
      query: ({ eventSlug, regId }) => ({
        url: `/events/${eventSlug}/registrations/${regId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { eventSlug }) => [
        { type: "EventRegs" as const, id: eventSlug },
      ],
    }),

    // POST /api/events/:slug/registrations/send-certificates
    // Starts a background certificate send job. Returns immediately with
    // a jobId — the actual sending happens in the background so the
    // HTTP connection never times out regardless of recipient count.
    // Poll getCertificateJobStatus with the jobId to track progress.
    sendCertificates: builder.mutation<
      {
        success: boolean;
        jobId: string;
        total: number;
        message: string;
      },
      { eventSlug: string; registrationIds?: string[] }
    >({
      query: ({ eventSlug, registrationIds }) => ({
        url: `/events/${eventSlug}/registrations/send-certificates`,
        method: "POST",
        body: registrationIds ? { registrationIds } : {},
      }),
    }),

    // GET /api/events/:slug/attendees  — admin: attended-only list with charts
    // Returns the same shape as getEventRegistrations but scoped to
    // status="attended" with extra certificate tracking fields.
    getEventAttendees: builder.query<
      AttendanceResponse,
      { eventSlug: string } & AttendanceFilters
    >({
      query: ({ eventSlug, ...params }) => ({
        url: `/events/${eventSlug}/attendees`,
        params,
      }),
      providesTags: (_r, _e, { eventSlug }) => [
        { type: "EventRegs" as const, id: `${eventSlug}-attendees` },
      ],
    }),

    // GET /api/events/:slug/registrations/certificate-job/:jobId
    // Poll the progress of a running or completed certificate send job.
    // Call this every 3–5 seconds after sendCertificates returns a jobId.
    // When status === "done" or "failed", stop polling.
    getCertificateJobStatus: builder.query<
      {
        success: boolean;
        jobId: string;
        status: "running" | "done" | "failed";
        total: number;
        sent: number;
        failed: number;
        remaining: number;
        failedList: {
          id: string;
          email: string;
          name: string;
          error: string;
        }[];
        elapsedSec: number;
        etaSeconds: number | null;
        startedAt: string;
        finishedAt: string | null;
      },
      { eventSlug: string; jobId: string }
    >({
      query: ({ eventSlug, jobId }) =>
        `/events/${eventSlug}/registrations/certificate-job/${jobId}`,
    }),
  }),
});

export const {
  useGetEventRegionsQuery,
  useGetEventProvincesQuery,
  useRegisterForEventMutation,
  useGetEventRegistrationsQuery,
  useGetEventAttendeesQuery,
  useUpdateRegistrationStatusMutation,
  useDeleteRegistrationMutation,
  useSendCertificatesMutation,
  useGetCertificateJobStatusQuery,
} = eventApi;
